// src/App.jsx
import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import TabButton from "./components/TabButton";
import AuthComponent from "./pages/AuthComponent";
import KronosHelper from "./components/KronosHelper";
import { ToastContainer } from "./components/Toast";
import { SubscriptionProvider } from "./SubscriptionContext";
import "./index.css";

// ── Sentry init ───────────────────────────────────────────────────────────────
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || "production",
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// ── Lazy-loaded pages (code splitting — each page is its own JS chunk) ────────
const DashboardTab      = lazy(() => import("./pages/DashboardTab"));
const AboutTab          = lazy(() => import("./pages/AboutTab"));
const SocialMediaTab    = lazy(() => import("./pages/SocialMediaTab"));
const ProposalsTab      = lazy(() => import("./pages/ProposalsTab"));
const CanvasTab         = lazy(() => import("./pages/CanvasTab"));
const PricingTab        = lazy(() => import("./pages/PricingTab"));
const AIReceptionistTab = lazy(() => import("./pages/AIReceptionistTab"));
const PrivacyPolicy     = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions   = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy      = lazy(() => import("./pages/RefundPolicy"));

// ── Page loading skeleton ─────────────────────────────────────────────────────
function PageSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6 animate-pulse">
      <div className="h-8 w-48 rounded-xl bg-white/5" />
      <div className="h-4 w-full rounded-lg bg-white/5" />
      <div className="h-4 w-3/4 rounded-lg bg-white/5" />
      <div className="grid grid-cols-3 gap-4 mt-4">
        {[1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5" />)}
      </div>
    </div>
  );
}

// Standalone legal pages with minimal chrome for direct URL access
function StandalonePrivacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <PrivacyPolicy />
      </div>
    </div>
  );
}

function StandaloneTerms() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <TermsConditions />
      </div>
    </div>
  );
}

function StandaloneRefund() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <RefundPolicy />
      </div>
    </div>
  );
}

function MainApp() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [token, setToken]         = useState(localStorage.getItem("token"));
  const [username, setUsername]   = useState(localStorage.getItem("username"));

  // Subtle muted colours — used only for active-tab underline, no bloom
  const COLORS = {
    dashboard:         "rgba(100,180,255,0.4)",
    about:             "rgba(100,200,255,0.4)",
    "ai-receptionist": "rgba(160,100,255,0.4)",
    "social-media":    "rgba(220,80,160,0.4)",
    proposals:         "rgba(140,80,255,0.4)",
    pricing:           "rgba(200,160,60,0.4)",
    login:             "rgba(60,200,160,0.4)",
    register:          "rgba(220,100,60,0.4)",
  };

  const handleAuth = ({ access_token, username }) => {
    localStorage.setItem("token", access_token);
    localStorage.setItem("username", username);
    setToken(access_token);
    setUsername(username);
    setActiveTab("dashboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    setToken(null);
    setUsername(null);
    setActiveTab("dashboard");
  };

  useEffect(() => {
    const onForceLogout = () => handleLogout();
    const onNavPricing = () => setActiveTab("pricing");
    const onNavSocial  = () => setActiveTab("social-media");
    window.addEventListener("auth:logout", onForceLogout);
    window.addEventListener("nav:pricing", onNavPricing);
    window.addEventListener("nav:social",  onNavSocial);
    return () => {
      window.removeEventListener("auth:logout", onForceLogout);
      window.removeEventListener("nav:pricing", onNavPricing);
      window.removeEventListener("nav:social",  onNavSocial);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderProtected = () => (
    <div className="text-center py-20 text-gray-400">
      <h2 className="text-2xl font-bold mb-4 text-gray-200">
        Please log in to access {activeTab.replace("-", " ")}.
      </h2>
      <TabButton tabName="login" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.login}>
        Login
      </TabButton>
    </div>
  );

  const renderContent = () => {
    if (!token && ["ai-receptionist", "social-media", "proposals", "fable-ai"].includes(activeTab)) {
      return renderProtected();
    }
    switch (activeTab) {
      case "dashboard":        return <DashboardTab onCtaClick={setActiveTab} />;
      case "about":            return <AboutTab onCtaClick={setActiveTab} />;
      case "ai-receptionist":  return <AIReceptionistTab />;
      case "social-media":     return <SocialMediaTab />;
      case "proposals":        return <ProposalsTab />;
      case "fable-ai":           return <CanvasTab />;
      case "pricing":          return <PricingTab onCtaClick={setActiveTab} />;
      case "login":            return <AuthComponent onAuth={handleAuth} />;
      case "register":         return <AuthComponent register onAuth={handleAuth} />;
      case "privacy":          return <PrivacyPolicy />;
      case "terms":            return <TermsConditions />;
      default:                 return <DashboardTab onCtaClick={setActiveTab} />;
    }
  };

  return (
    <SubscriptionProvider>
    <div className="min-h-screen bg-transparent flex items-center justify-center p-2 sm:p-4 relative overflow-hidden">

      {/* Background is now handled by CSS starry night animation in index.html/index.css */}

      {/* App shell - more transparent to show starry background */}
      <div className="relative z-10 w-full max-w-7xl bg-[#111114]/40 rounded-xl sm:rounded-2xl border border-white/[0.06] shadow-2xl">

        {/* Header */}
        <header style={{borderBottom: '1px solid rgba(255,255,255,0.08)', width: '100%', background: 'rgba(17,17,20,0.6)', position: 'relative'}}>
          {/* Desktop header - visible on desktop, hidden on mobile */}
          <div className="desktop-header">
            {/* Logo — overflows header intentionally for visual impact */}
            <div className="flex-shrink-0 relative logo-glow" style={{zIndex: 10}}>
              <img
                src="/Kronos-Main.png"
                alt="Kronos Assistant"
                style={{height: "160px", marginTop: "-32px", marginBottom: "-32px"}}
                className="w-auto object-contain logo-glow-img"
              />
            </div>

            {/* Navigation */}
            <nav className="flex gap-1 lg:gap-2 items-center justify-center flex-1">
              {["dashboard","ai-receptionist","social-media","proposals","fable-ai","pricing","about"].map((tab) => (
                <TabButton
                  key={tab}
                  tabName={tab}
                  activeTab={activeTab}
                  onClick={setActiveTab}
                  glowColor={COLORS[tab]}
                >
                  <span className="text-xs lg:text-sm whitespace-nowrap px-1 lg:px-2">
                    {tab.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </TabButton>
              ))}
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              {token && (
                <span className="text-sm text-gray-400 whitespace-nowrap">
                  {username}
                </span>
              )}
              {!token ? (
                <div className="flex gap-1 lg:gap-2">
                  <TabButton tabName="login" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.login}>
                    <span className="text-xs lg:text-sm">Login</span>
                  </TabButton>
                  <TabButton tabName="register" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.register}>
                    <span className="text-xs lg:text-sm">Sign Up</span>
                  </TabButton>
                </div>
              ) : (
                <button
                  onClick={handleLogout}
                  className="px-3 lg:px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs lg:text-sm font-medium hover:bg-white/10 hover:text-white transition-all hover:scale-105"
                >
                  Logout
                </button>
              )}
            </div>
          </div>

          {/* Mobile header - visible only on mobile */}
          <div className="mobile-header">
            {/* Top row: Logo and Auth */}
            <div className="flex items-center justify-between px-3 py-1" style={{minHeight: "72px"}}>
              <img
                src="/Kronos-Main.png"
                alt="Kronos Assistant"
                className="w-auto object-contain logo-glow-img"
                style={{ height: "100px", marginTop: "-14px", marginBottom: "-14px" }}
              />
              <div className="flex items-center gap-2">
                {token && (
                  <span className="text-xs text-gray-400 max-w-[80px] truncate">
                    {username}
                  </span>
                )}
                {!token ? (
                  <div className="flex gap-1.5">
                    <TabButton tabName="login" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.login}>
                      <span className="text-xs px-2">Login</span>
                    </TabButton>
                    <TabButton tabName="register" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.register}>
                      <span className="text-xs px-2">Sign Up</span>
                    </TabButton>
                  </div>
                ) : (
                  <button
                    onClick={handleLogout}
                    className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs font-medium hover:bg-white/10"
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>

            {/* Bottom row: Navigation tabs - scrollable */}
            <div className="overflow-x-auto scrollbar-hide border-t border-white/[0.08]">
              <nav className="flex gap-1 px-4 py-2 min-w-max">
                {["dashboard","ai-receptionist","social-media","proposals","fable-ai","pricing","about"].map((tab) => (
                  <TabButton
                    key={tab}
                    tabName={tab}
                    activeTab={activeTab}
                    onClick={setActiveTab}
                    glowColor={COLORS[tab]}
                  >
                    <span className="text-xs whitespace-nowrap px-2 py-1">
                      {tab.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </TabButton>
                ))}
              </nav>
            </div>
          </div>
        </header>

        {/* Page content — Suspense wraps all lazy-loaded tabs */}
        <main className="px-4 sm:px-6 md:px-10 py-6 sm:py-8 min-h-[60vh]">
          <Suspense fallback={<PageSkeleton />}>
            {renderContent()}
          </Suspense>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/[0.08] px-4 sm:px-6 py-4 flex flex-col items-center gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap justify-center">
            <a href="https://github.com/DevX-House" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">GitHub</a>
            <a href="https://pk.linkedin.com/company/devxhouseco" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">LinkedIn</a>
            <a href="https://www.instagram.com/devxhouse/" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">Instagram</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Terms & Conditions</a>
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Refund Policy</a>
            <a href="mailto:kronos@devxhouse.com" className="hover:text-cyan-400 transition-colors">Contact</a>
          </div>
          <span className="text-center text-gray-400 font-medium text-xs">
            Built with passion by DevX House
          </span>
          <span>© 2026 Kronos AI. All rights reserved.</span>
        </footer>

      </div>

    </div>
    </SubscriptionProvider>
  );
}

// Main export with routing + Sentry error boundary
export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white p-8">
        <div className="text-center space-y-4 max-w-md">
          <div className="text-5xl">⚠️</div>
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="text-gray-400">We've been notified and are working on a fix.</p>
          <button onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-semibold hover:bg-cyan-500/30 transition-all">
            Reload Page
          </button>
        </div>
      </div>
    }>
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/privacy" element={<StandalonePrivacy />} />
            <Route path="/terms" element={<StandaloneTerms />} />
            <Route path="/refund" element={<StandaloneRefund />} />
            <Route path="/*" element={<MainApp />} />
          </Routes>
        </Suspense>
        <KronosHelper />
        <ToastContainer />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
